/* eslint-disable react/display-name, @typescript-eslint/no-empty-function */
/*
 * Copyright © 2020-2023 Ocean Galaxy Inc. All Rights Reserved.
 * @Description:
 * @LastEditors: yongqiang
 * @LastEditTime: 2025-08-31 11:45:07
 */
import { Button, Layout, Modal } from 'antd'
import classNames from 'classnames'
import React, { useState, useEffect, useMemo, useRef } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '../../../app/hooks'
import {
  addPhotosToFolders,
  fetchGroupFolders,
  getFolderInfo
} from '../../../actions/folder'
import Filter from './Filter'
// import PhotosList from '../components/PhotosList'
import PhotosList from './PhotosList'
import { fetchPhotos, setQueryFilter } from '../../../actions/photo'
import actionTypes from '../../../constants/actionTypes'
import CONFIG from '../../../constants/config'
import { capturePromiseException } from '../../../utils/sentryUtils'
import sensors, { sensorsRole } from '../../../utils/sensors'
import DownloadHistory from '../../components/DownloadHistory'
import { buildFetchPhotosParams } from '@/pages/components/photoLib/photo'
import { InfoCircleOutlined, LeftOutlined } from '@ant-design/icons'
import type { Grid } from 'react-virtualized'

import PageHeader from '@/components/PageHeader'
const { Content } = Layout

// FolderInfo 类型
interface FolderInfo {
  folderID?: number
  folderName?: string
  parentFolderID?: number
  folderType?: number
  [key: string]: any
}

export default () => {
  const navigate = useNavigate()
  const { id: idParam } = useParams<{ id: string }>()
  const id = Number(idParam)
  const filter = useAppSelector((state) => state.photo.filter)
  const groupID = useAppSelector((state) => state.group.groupId)
  const userId = useAppSelector((state) => state.main.user?.user_id)
  const [folderInfo, setFolderInfo] = useState<FolderInfo>({})
  const myRole = useAppSelector((state) => state.group.myRole)
  const checkedPhotos = useAppSelector((state) => state.photo.checkedPhotos)
  const unCheckedPhotos = useAppSelector((state) => state.photo.unCheckedPhotos)
  const photos = useAppSelector((state) => state.photo.photos)
  const checkAll = useAppSelector((state) => state.photo.checkAll)

  const dispatch = useAppDispatch()
  const photoListRef = useRef<Grid>(null)

  const folderName = useMemo(() => {
    return folderInfo.folderName
  }, [folderInfo])

  const handleCancelChecked = () => {
    dispatch({ type: actionTypes.CLEAR_CHECKED_PHOTO })
    dispatch({ type: actionTypes.CLEAR_UNCHECKED_PHOTO })
    dispatch({ type: actionTypes.SET_CHECK_ALL, data: false })
  }

  useEffect(() => {
    // dispatch(
    //   fetchGroupFolders({
    //     groupID
    //   })
    // )
    dispatch(
      getFolderInfo({
        groupID,
        folderID: id
      })
    ).then((data) => {
      setFolderInfo(data)
    })

    handleCancelChecked()
  }, [])

  useEffect(() => {
    const groupId = localStorage.getItem('group_id')

    if (!groupId) {
      return navigate(`/choose_group`, { replace: true })
    }

    if (myRole !== CONFIG.memberRoleTypes.ALL) {
      let queryFilter = filter
      if (
        myRole !== CONFIG.memberRoleTypes.ADMIN &&
        myRole !== CONFIG.memberRoleTypes.MASTER_ADMIN
      ) {
        // 因为照片查看接入了权限中心，但文件夹相关权限还不支持。特殊处理：非管理员时，只能看到自己的照片，和之前保持一致
        queryFilter = {
          ...filter,
          departmentUsers: [{ userID: userId }]
        }
      }
      dispatch(setQueryFilter(queryFilter))
      dispatch(
        fetchPhotos(buildFetchPhotosParams(queryFilter, groupId, ''))
      ).catch(capturePromiseException)
    }
  }, [myRole])

  useEffect(() => {
    return () => {
      //重置查询条件和数据
      dispatch({ type: actionTypes.RESET_FILTER })
      dispatch({ type: actionTypes.SET_PHOTOS, data: [] })
      dispatch({ type: actionTypes.SET_PHOTOS_NUM, data: 0 })
      dispatch({ type: actionTypes.CLEAR_CHECKED_PHOTO })
      dispatch({ type: actionTypes.CLEAR_UNCHECKED_PHOTO })
      dispatch({ type: actionTypes.SET_CHECK_ALL, data: false })
    }
  }, [])

  const handleSensorOfClickEvent = (
    clickItem: string,
    restProperties?: Record<string, any>
  ) => {
    sensors.track('web_workgroup_add_photos_click', {
      clickItem,
      groupID,
      role: sensorsRole(myRole),
      folderID: id,
      folderName,
      ...restProperties
    })
  }

  const handleSubmit = () => {
    handleSensorOfClickEvent('finish')

    const saReport = (clickItem) => {
      sensors.track('web_get_action_pop_workgroup_upload_photo_limit', {
        clickItem,
        role: sensorsRole(myRole),
        groupID
      })
    }

    let photoIDs: string[] = []

    if (checkAll) {
      const all = photos.map((item) => item.seq.toString())
      const unchecked = Object.keys(unCheckedPhotos)

      photoIDs = all.filter((item) => !unchecked.includes(item))
    } else {
      photoIDs = Object.keys(checkedPhotos)
    }

    const submit = () => {
      dispatch(
        addPhotosToFolders({
          groupID,
          folderIDs: [id],
          photoIDs: photoIDs.slice(0, CONFIG.folderConfig.batchAddPhotoLimit),
          source: 17 // 区分添加照片的来源，3.0.40照片文件夹需求此处的值为7，85版本批量上传照片需求也使用了7，和此处冲突了。2024年1月7日把此处改为17
        })
      ).then(() => {
        dispatch({
          type: actionTypes.CLEAR_CHECKED_PHOTO
        })

        navigate(`/photo/folder/detail/${id}`)
      })
    }

    if (photoIDs.length > CONFIG.folderConfig.batchAddPhotoLimit) {
      saReport('show')

      Modal.confirm({
        title: `一次最多可添加${CONFIG.folderConfig.batchAddPhotoLimit}张`,
        onOk: () => {
          saReport(`addFirst${CONFIG.folderConfig.batchAddPhotoLimit}`)
          submit()
        },
        onCancel: () => {
          saReport('cancel')
        },
        okText: `添加所选前${CONFIG.folderConfig.batchAddPhotoLimit}张`,
        icon: <InfoCircleOutlined />
      })
    } else {
      submit()
    }
  }

  return (
    <Layout className={classNames('Photo', 'photo-folder-add')}>
      <PageHeader
        ghost={false}
        style={{ borderBottom: '1px solid rgb(235, 237, 240)' }}
        title={
          <div
            className="title"
            onClick={() => {
              handleSensorOfClickEvent('back')
              navigate(`/photo/folder/detail/${id}`)
            }}
          >
            <LeftOutlined style={{ marginRight: 8 }} />
            添加至「{folderName}」
          </div>
        }
        extra={
          <Button
            type="primary"
            onClick={handleSubmit}
            disabled={Object.keys(checkedPhotos).length === 0 && !checkAll}
            style={{
              paddingLeft: 27,
              paddingRight: 27,
              height: 36
            }}
          >
            完成
          </Button>
        }
      />
      <Content className="content">
        <Filter
          photoListRef={photoListRef}
          handleSensorOfClickEvent={handleSensorOfClickEvent}
        />
        <PhotosList
          photoListRef={photoListRef}
          handleSensorOfClickEvent={handleSensorOfClickEvent}
        />
        <DownloadHistory />
      </Content>
    </Layout>
  )
}
